import React, { useState, useMemo, useEffect, useRef, useContext } from "react";
import {
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  TextField,
  IconButton,
  Stack,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import ScrollView from "devextreme-react/scroll-view";
import { baseAxios, Context } from "../../utils/config";
import { toast } from "react-hot-toast";
import AsyncAutocompleteAddClient from "../../components/autocompleteAddClient/autocompleteAddClient";
import AsyncAutocompleteStylists from "../../components/autocompleteStylists/autocompleteStylists";
import {
  dialogPaperSx,
  dialogTitleSx,
  dialogCloseIconSx,
  primaryBtnSx,
  secondaryBtnSx,
} from "./schedulerDialogStyles";
import { colors } from "../../theme/colors";
import {
  applyStylistPricesToItems,
  resolveWalkInItemPrice,
  stylistServicePriceMap,
  WALK_IN_ITEM_TYPES,
} from "./walkInPricing";

const ITEM_TYPES = WALK_IN_ITEM_TYPES;

const PAGE_COUNT = 20;

// Custom Lazy Loading Autocomplete for Products/Services
const ProductServiceAutocomplete = ({
  options,
  value,
  onChange,
  disabled = false,
  loading = false,
}) => {
  const [openList, setOpenList] = useState(false);
  const [text, setText] = useState("");
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [displayCount, setDisplayCount] = useState(PAGE_COUNT);
  const listRef = useRef(null);
  const textRef = useRef(null);
  const containerRef = useRef(null);

  // Filter options based on search
  useEffect(() => {
    const searchTerm = text.toLowerCase();
    const filtered = options.filter(
      (option) =>
        option.name?.toLowerCase().includes(searchTerm) ||
        option.shortcut?.toLowerCase().includes(searchTerm) ||
        option.brand?.toLowerCase().includes(searchTerm) ||
        option.description?.toLowerCase().includes(searchTerm)
    );
    setFilteredOptions(filtered);
    setDisplayCount(PAGE_COUNT); // Reset display count on new search
  }, [text, options]);

  // Get items to display (up to displayCount)
  const displayedItems = useMemo(() => {
    return filteredOptions.slice(0, displayCount);
  }, [filteredOptions, displayCount]);

  useEffect(() => {
    if (value) {
      setText(value.name || "");
    } else {
      setText("");
    }
  }, [value]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpenList(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleScroll = (e) => {
    const scrollViewInstance = listRef.current?.instance;
    if (!scrollViewInstance) return;

    // Load more when scrolled to bottom
    if (
      scrollViewInstance.scrollHeight() <=
      scrollViewInstance.clientHeight() + e.component.scrollTop() + 10
    ) {
      if (displayCount < filteredOptions.length) {
        setDisplayCount((prev) => prev + PAGE_COUNT);
      }
    }
  };

  const handleSelect = (option) => {
    setText(option.name);
    onChange(option);
    setOpenList(false);
  };

  const handleClear = () => {
    setText("");
    onChange(null);
    textRef.current?.focus();
  };

  // Get secondary text based on type
  const getSecondaryText = (option) => {
    if (option.type === ITEM_TYPES.SERVICE) {
      return option.shortcut || "";
    } else {
      return option.description || option.brand || "";
    }
  };

  return (
    <Box ref={containerRef} sx={{ position: "relative", flex: 1 }}>
      <TextField
        inputRef={textRef}
        size="small"
        fullWidth
        value={text}
        onChange={(e) => setText(e.target.value)}
        onFocus={() => setOpenList(true)}
        placeholder="Search products or services"
        disabled={disabled}
        InputProps={{
          startAdornment: value?.type ? (
            <Chip
              label={value.type === ITEM_TYPES.SERVICE ? "Service" : "Product"}
              size="small"
              sx={{
                mr: 0.5,
                width: 80,
                maxWidth: 80,
                height: 18,
                minWidth: 18,
                fontSize: "10px",
                bgcolor: value.type === ITEM_TYPES.SERVICE ? "#1976d2" : "#9c27b0",
                color: "white",
              }}
            />
          ) : null,
          endAdornment: (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {loading && <CircularProgress size={16} sx={{ mr: 0.5 }} />}
              {value && (
                <IconButton size="small" onClick={handleClear}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton size="small" onClick={() => setOpenList(!openList)}>
                <KeyboardArrowDownIcon fontSize="small" />
              </IconButton>
            </Box>
          ),
        }}
      />
      {openList && (
        <Box
          className="scheduler-dropdown"
          sx={{
            position: "fixed",
            width: containerRef.current?.offsetWidth || 300,
            zIndex: 9999,
            bgcolor: "background.paper",
            boxShadow: 4,
            borderRadius: 1,
            mt: 0.5,
          }}
          style={{
            top: containerRef.current ? containerRef.current.getBoundingClientRect().bottom + 4 : 0,
            left: containerRef.current ? containerRef.current.getBoundingClientRect().left : 0,
          }}
        >
          <ScrollView
            ref={listRef}
            height={220}
            onScroll={handleScroll}
          >
            <List component="nav" dense>
              {displayedItems.map((option) => {
                const isSelected = value?.id === option.id;
                const secondaryText = getSecondaryText(option);
                return (
                  <ListItemButton
                    key={option.id}
                    selected={isSelected}
                    onClick={() => handleSelect(option)}
                    sx={{ py: 0.75 }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={500} noWrap>
                        {option.name}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.25 }}>
                        <Chip
                          label={option.type === ITEM_TYPES.SERVICE ? "Service" : "Product"}
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: "0.6rem",
                            bgcolor: option.type === ITEM_TYPES.SERVICE ? "#1976d2" : "#9c27b0",
                            color: "white",
                            "& .MuiChip-label": { px: 0.75 },
                          }}
                        />
                        {secondaryText && (
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: "0.7rem" }}>
                            {secondaryText}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Typography variant="body2" fontWeight={500} sx={{ ml: 1, flexShrink: 0 }}>
                      {option.msrp != null && option.msrp !== ""
                        ? `$${Number(option.msrp).toFixed(2)}`
                        : ""}
                    </Typography>
                    {isSelected && <CheckIcon fontSize="small" sx={{ ml: 0.5, color: "primary.main" }} />}
                  </ListItemButton>
                );
              })}
              {displayedItems.length === 0 && (
                <ListItemButton disabled>
                  <ListItemText
                    primary="No items found"
                    secondary="Try a different search"
                  />
                </ListItemButton>
              )}
              {displayCount < filteredOptions.length && (
                <ListItemButton disabled sx={{ justifyContent: "center" }}>
                  <Typography variant="caption" color="text.secondary">
                    Scroll for more ({filteredOptions.length - displayCount} remaining)
                  </Typography>
                </ListItemButton>
              )}
            </List>
          </ScrollView>
        </Box>
      )}
    </Box>
  );
};

const TransactionFormModal = ({
  open,
  onClose,
  onConfirm,
  dataset,
  initialData,
  refreshData,
}) => {
  const { location: globalLocation } = useContext(Context);
  const hasSpecificLocation = Number(globalLocation?.location_id) > 0;
  const selectedLocation = useMemo(() => {
    if (!hasSpecificLocation) return null;
    const fromDataset = (dataset?.locations || []).find(
      (loc) => loc.location_id === globalLocation.location_id
    );
    return fromDataset || globalLocation;
  }, [hasSpecificLocation, globalLocation, dataset?.locations]);
  const locationName = selectedLocation?.location_name || "";

  // Local state for the transaction form
  const [clientId, setClientId] = useState(initialData?.clientId || null);
  const [selectedClient, setSelectedClient] = useState(initialData?.selectedClient || null);
  const [items, setItems] = useState(initialData?.items || []);
  const [selectedStylist, setSelectedStylist] = useState(initialData?.selectedStylist || null);
  const [stylistId, setStylistId] = useState(initialData?.stylistId || null);

  // Data for dropdowns
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [stylistServices, setStylistServices] = useState([]);
  const [loadingStylistServices, setLoadingStylistServices] = useState(false);

  const stylistServicePrices = useMemo(
    () => stylistServicePriceMap(stylistServices),
    [stylistServices]
  );

  // Combined list of services and products for the dropdown
  const combinedOptions = useMemo(() => {
    const serviceOptions = (dataset?.services || [])
      .filter((service) => Boolean(service))
      .map((service) => {
        const key = String(service.service_id);
        const configured = Object.prototype.hasOwnProperty.call(
          stylistServicePrices,
          key
        );
        const msrp = configured ? Number(stylistServicePrices[key]) : null;
        return {
          id: `service-${service.service_id}`,
          type: ITEM_TYPES.SERVICE,
          referenceId: service.service_id,
          name: service.service_name,
          shortcut: service.service_shortcut,
          description: service.service_shortcut,
          msrp: Number.isFinite(msrp) ? msrp : null,
        };
      });

    const productOptions = products
      .filter((product) => Boolean(product))
      .map((product) => ({
      id: `product-${product.product_id}`,
      type: ITEM_TYPES.PRODUCT,
      referenceId: product.product_id,
      name: product.name || product.product_name || `Product #${product.product_id}`,
      brand: product.brand || product.brand_name,
      description: product.container_desc || product.brand_name || "", // Use container_desc for products
      msrp: product.msrp || product.product_msrp,
    }));

    return [...serviceOptions, ...productOptions];
  }, [dataset?.services, products, stylistServicePrices]);

  // Load products from inventory
  useEffect(() => {
    const loadProducts = async () => {
      setLoadingProducts(true);
      try {
        // Use /inventory endpoint which returns products with inventory data
        const response = await baseAxios.get("/inventory");
        if (response.status === 200 && response.data?.products) {
          // Map inventory data to product format
          const productMap = new Map();
          response.data.products.forEach((item) => {
            if (!productMap.has(item.product_id)) {
              productMap.set(item.product_id, {
                product_id: item.product_id,
                product_name: item.product_name,
                brand_name: item.brand_name,
                container_desc: item.container_desc || item.product_container_desc || "",
                product_msrp: item.inventory_product_price || item.product_cost || 0,
              });
            }
          });
          setProducts(Array.from(productMap.values()));
        }
      } catch (error) {
        console.error("Error loading products:", error);
        // Don't show error toast - products are optional
      } finally {
        setLoadingProducts(false);
      }
    };
    if (open) {
      loadProducts();
    }
  }, [open]);

  useEffect(() => {
    if (!open || !stylistId) {
      setStylistServices([]);
      setLoadingStylistServices(false);
      return undefined;
    }

    let cancelled = false;
    const loadStylistServices = async () => {
      setLoadingStylistServices(true);
      try {
        const response = await baseAxios.get(
          `/services/getUserServices/${stylistId}`
        );
        if (cancelled) return;
        const list = (
          Array.isArray(response.data) ? response.data : []
        ).filter(Boolean);
        setStylistServices(list);
      } catch (error) {
        console.error("Error loading stylist services:", error);
        if (!cancelled) {
          setStylistServices([]);
          toast.error("Could not load stylist service prices");
        }
      } finally {
        if (!cancelled) setLoadingStylistServices(false);
      }
    };

    loadStylistServices();
    return () => {
      cancelled = true;
    };
  }, [open, stylistId]);

  useEffect(() => {
    setItems((prev) => applyStylistPricesToItems(prev, stylistServicePrices));
  }, [stylistId, stylistServicePrices]);

  // Restore initial data when modal opens with initialData
  useEffect(() => {
    if (open && initialData) {
      setClientId(initialData.clientId);
      setSelectedClient(initialData.selectedClient);
      setSelectedStylist(initialData.selectedStylist || null);
      setStylistId(initialData.stylistId || null);
      // Properly restore items with selectedOption
      const restoredItems = (initialData.items || []).map(item => ({
        id: item.id || `item-${Date.now()}-${Math.random()}`,
        type: item.type,
        referenceId: item.referenceId,
        name: item.name,
        price: item.price?.toString() || "",
        selectedOption: item.selectedOption,
      }));
      setItems(restoredItems);
    }
  }, [open, initialData]);

  const prevLocationIdRef = useRef(selectedLocation?.location_id);
  useEffect(() => {
    const locationId = selectedLocation?.location_id;
    if (
      open &&
      prevLocationIdRef.current != null &&
      locationId !== prevLocationIdRef.current
    ) {
      setSelectedClient(null);
      setClientId(null);
      setSelectedStylist(null);
      setStylistId(null);
    }
    prevLocationIdRef.current = locationId;
  }, [open, selectedLocation?.location_id]);

  // Calculate total
  const totalAmount = useMemo(() => {
    return items.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0);
  }, [items]);

  // Validation
  const isValid = useMemo(() => {
    return (
      selectedLocation !== null &&
      selectedLocation?.location_id !== -1 &&
      stylistId !== null &&
      clientId !== null &&
      items.length > 0 &&
      items.every((item) => parseFloat(item.price) > 0)
    );
  }, [selectedLocation, clientId, items, stylistId]);

  const handleStylistChange = (value) => {
    setSelectedStylist(value);
    setStylistId(value?.user_id ?? value?.id ?? null);
  };

  // Handle client selection
  const handleClientChange = (value) => {
    setSelectedClient(value);
    setClientId(value?.client_id || null);
  };

  // Handle adding a new empty item row
  const handleAddItemRow = () => {
    setItems((prev) => [
      ...prev,
      {
        id: `item-${Date.now()}`,
        type: null,
        referenceId: null,
        name: "",
        price: "",
        selectedOption: null,
      },
    ]);
  };

  // Handle item selection from combined dropdown
  const handleItemSelect = (itemId, selectedOption) => {
    const price = resolveWalkInItemPrice(selectedOption, stylistServicePrices);
    const pricedOption = selectedOption
      ? {
          ...selectedOption,
          msrp: price === "" ? null : Number(price),
        }
      : null;
    setItems((prev) =>
      prev.map((item) => {
        if (item.id === itemId) {
          return {
            ...item,
            type: selectedOption?.type || null,
            referenceId: selectedOption?.referenceId || null,
            name: selectedOption?.name || "",
            price,
            selectedOption: pricedOption,
          };
        }
        return item;
      })
    );
  };

  // Handle price change for an item
  const handleItemPriceChange = (itemId, newPrice) => {
    const value = newPrice.replace(/[^0-9.]/g, "");
    const decimalCount = (value.match(/\./g) || []).length;
    if (decimalCount > 1) return;
    const parts = value.split(".");
    if (parts[1] && parts[1].length > 2) return;
    
    setItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, price: value } : item
      )
    );
  };

  // Remove item from list
  const handleRemoveItem = (itemId) => {
    setItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  // Handle confirm - pass data to summary modal
  const handleConfirm = () => {
    if (!isValid) {
      if (!hasSpecificLocation || !selectedLocation) {
        toast.error("Please select a specific location");
      } else if (!stylistId) {
        toast.error("Please select a stylist");
      } else if (!clientId) {
        toast.error("Please select a client");
      } else if (items.length === 0) {
        toast.error("Please add at least one item");
      } else {
        toast.error("All items must have a valid price");
      }
      return;
    }

    const transactionData = {
      clientId,
      selectedClient,
      selectedLocation,
      selectedStylist,
      stylistId,
      clientName: selectedClient?.client_name,
      clientEmail: selectedClient?.client_email || "",
      items: items.map((item) => ({
        id: item.id,
        type: item.type,
        referenceId: item.referenceId,
        name: item.name,
        price: parseFloat(item.price),
        selectedOption: item.selectedOption,
      })),
      totalAmount,
      locationId: selectedLocation?.location_id,
      locationName: selectedLocation?.location_name,
    };

    onConfirm(transactionData);
  };

  // Handle close and reset
  const handleClose = () => {
    // Reset all state
    resetForm();
    onClose();
  };

  // Reset form to initial state
  const resetForm = () => {
    setClientId(null);
    setSelectedClient(null);
    setSelectedStylist(null);
    setStylistId(null);
    setStylistServices([]);
    setItems([]);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle sx={dialogTitleSx}>
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Typography fontWeight="bold" color="inherit" fontSize={16}>
            Walk-in Transaction{locationName ? ` · ${locationName}` : ""}
          </Typography>
          <IconButton onClick={handleClose} size="small" sx={dialogCloseIconSx}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1}>
          {/* Location is taken from the global header selector */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              border: `1px solid ${hasSpecificLocation ? colors.gold : "#d32f2f"}`,
              bgcolor: hasSpecificLocation
                ? "rgba(197,160,111,0.12)"
                : "rgba(211,47,47,0.08)",
              borderRadius: "6px",
              px: 1.5,
              py: 1,
            }}
          >
            <LocationOnIcon
              sx={{
                color: hasSpecificLocation ? colors.gold : "#d32f2f",
                fontSize: 20,
              }}
            />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Location
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {locationName || "Select a specific location in the header"}
              </Typography>
            </Box>
          </Box>

          {/* Stylist Selection */}
          <Box sx={{ position: "relative", zIndex: 2, pt: 2 }}>
            <AsyncAutocompleteStylists
              label="Select Stylist"
              value={selectedStylist}
              onChange={handleStylistChange}
              disabled={false}
              selectedLocation={selectedLocation}
            />
          </Box>

          {/* Client Selection */}
          <Box>
            <AsyncAutocompleteAddClient
              label="Select Client"
              // Load clients lazily from the server by location instead of
              // relying on the current scheduler client dataset.
              datasets={[]}
              keyField="client_id"
              titleField="client_name"
              subtitleField="org_name"
              phoneField="client_mobile_phone_number"
              emailField="client_email"
              sortField="client_name"
              value={selectedClient}
              onChange={handleClientChange}
              disabled={!selectedLocation || selectedLocation?.location_id === -1}
              selectedLocation={selectedLocation}
              onClientCreated={(newClient) => {
                if (refreshData) refreshData();
              }}
            />
          </Box>

          {/* Products or Services Section */}
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Typography variant="body2" gutterBottom>
              Products or Services <span style={{ color: "#d32f2f" }}>*</span>
            </Typography>
            
            {/* Items List */}
            <Stack spacing={1.5}>
              {items.map((item) => (
                <Stack key={item.id} direction="row" spacing={1} alignItems="flex-start">
                  {/* Combined Autocomplete for Service/Product */}
                  <ProductServiceAutocomplete
                    options={combinedOptions}
                    value={item.selectedOption}
                    onChange={(value) => handleItemSelect(item.id, value)}
                    loading={loadingProducts || loadingStylistServices}
                  />
                  
                  {/* Price Field */}
                  <TextField
                    value={item.price}
                    onChange={(e) => handleItemPriceChange(item.id, e.target.value)}
                    placeholder=""
                    size="small"
                    sx={{ width: 100 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                  
                  {/* Delete Button */}
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveItem(item.id)}
                    sx={{ mt: 0.5 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              
              {/* Add Button */}
              <Button
                variant="text"
                startIcon={<AddIcon />}
                onClick={handleAddItemRow}
                sx={{ alignSelf: "flex-start", textTransform: "none" }}
              >
                Add item
              </Button>
            </Stack>
          </Box>

          {/* Total */}
          {items.length > 0 && items.some(item => item.price && parseFloat(item.price) > 0) && (
            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ pt: 1.5, borderTop: "1px dashed #999" }}
              >
                <Typography variant="body1" fontWeight="bold">
                  Total Amount
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  ${totalAmount.toFixed(2)}
                </Typography>
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={resetForm}
          variant="outlined"
          color="error"
          sx={{ mr: "auto", textTransform: "none", fontWeight: 600, borderRadius: "6px" }}
        >
          Reset
        </Button>
        <Button onClick={handleClose} sx={secondaryBtnSx}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!isValid}
          sx={primaryBtnSx}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransactionFormModal;
